// Swiss number formatting per Corporate Design:
//   * apostrophe thousands separator (1'000)
//   * comma decimal (1'234,5)
// Plus a couple of helpers used everywhere on the dashboard. Number and date
// formats are a brand rule and therefore identical in both dashboard
// languages; only words go through the dictionary.

import { t, tOr } from "@/i18n";

const NUMBER = new Intl.NumberFormat("de-CH", {
  maximumFractionDigits: 0,
});

const DECIMAL_1 = new Intl.NumberFormat("de-CH", {
  maximumFractionDigits: 1,
});

const DECIMAL_2 = new Intl.NumberFormat("de-CH", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const DECIMAL_2_TO_4 = new Intl.NumberFormat("de-CH", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 4,
});

const PERCENT_1 = new Intl.NumberFormat("de-CH", {
  style: "percent",
  maximumFractionDigits: 1,
});

const TIME = new Intl.DateTimeFormat("de-CH", {
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

const DATETIME = new Intl.DateTimeFormat("de-CH", {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

export const formatInt = (n: number): string => NUMBER.format(n);
// de-CH CLDR renders the decimal separator as a period ("0.3"), but the
// gateway.one Corporate Design mandates a comma for general numbers ("0,3").
// Normalise it here — idempotent (a comma stays a comma) and deterministic
// regardless of the host's ICU build. Currency keeps the period per CD
// ("CHF 1'250.00"), so formatUsd is left untouched.
export const formatDecimal = (n: number): string => DECIMAL_1.format(n).replace(".", ",");
export const formatUsd = (n: number): string => `CHF ${DECIMAL_2.format(n)}`;
// Cost per turn or per person sits below a cent; two places would read CHF 0.00.
export const formatUsdFine = (n: number): string =>
  Math.abs(n) >= 1 ? formatUsd(n) : `CHF ${DECIMAL_2_TO_4.format(n)}`;
// A ratio read against a threshold: two places, so 1,08 and 1,11 read apart.
export const formatRatio = (n: number): string => DECIMAL_2.format(n).replace(".", ",");
export const formatPercent = (ratio: number): string => PERCENT_1.format(ratio);

export const formatTime = (iso: string): string => TIME.format(new Date(iso));
export const formatDateTime = (iso: string): string =>
  DATETIME.format(new Date(iso));

// Human-friendly duration from a millisecond delta, short units in the
// active language: "0 Min", "3 Min", "1 Std 12 Min", "2 Std". Negative clamps to 0.
export const formatDuration = (ms: number): string => {
  const totalMin = Math.max(0, Math.round(ms / 60000));
  const hours = Math.floor(totalMin / 60);
  const minutes = totalMin % 60;
  const min = t("duration.min");
  const hrs = t("duration.hours");
  if (hours === 0) return `${minutes} ${min}`;
  if (minutes === 0) return `${hours} ${hrs}`;
  return `${hours} ${hrs} ${minutes} ${min}`;
};

// Seconds-granular duration for chat spans (short chats live under a minute):
// "45 Sek", "4 Min 20 Sek", "1 Std 12 Min". Negative clamps to 0.
export const formatDurationSeconds = (seconds: number): string => {
  const total = Math.max(0, Math.round(seconds));
  const sec = t("duration.sec");
  const min = t("duration.min");
  const hrs = t("duration.hours");
  if (total < 60) return `${total} ${sec}`;
  const totalMin = Math.floor(total / 60);
  const rest = total % 60;
  if (totalMin < 60) {
    return rest === 0 ? `${totalMin} ${min}` : `${totalMin} ${min} ${rest} ${sec}`;
  }
  const hours = Math.floor(totalMin / 60);
  const minutes = totalMin % 60;
  return minutes === 0 ? `${hours} ${hrs}` : `${hours} ${hrs} ${minutes} ${min}`;
};

// Clip a plain-text string to `maxChars`, appending an ellipsis when cut.
// Used by the Recent Q&A panel for the collapsed answer preview.
export const truncateText = (text: string, maxChars: number): string =>
  text.length <= maxChars ? text : text.slice(0, maxChars) + "…";

// User-facing display labels for internal chat-mode ids. The stored
// `chat_mode` value stays the internal id everywhere (Firestore, API,
// analytics); only the dashboard label is humanised so it matches the widget
// mode menu (e.g. "faq" → "FAQ", "discovery" → "Orientierung"). Unknown ids
// fall through unchanged.
export const modeLabel = (mode: string): string => tOr(`mode.${mode}`, mode);

// Partner tenants. A configured tenant id IS its label — brand names are
// written the way the brand writes them (gateway.one stays lowercase), so no
// title-casing here. Only the two built-in buckets get prose, because
// "unknown" / "unmapped" mean nothing to a reader who hasn't read the code.
export const tenantLabel = (tenant: string): string =>
  tenant ? tOr(`tenant.${tenant}`, tenant) : t("tenant.all");

export const formatRelative = (iso: string): string => {
  const ms = Date.now() - new Date(iso).getTime();
  const sec = Math.round(ms / 1000);
  if (sec < 60) return t("relative.seconds", { n: sec });
  const min = Math.round(sec / 60);
  if (min < 60) return t("relative.minutes", { n: min });
  const hr = Math.round(min / 60);
  if (hr < 48) return t("relative.hours", { n: hr });
  const days = Math.round(hr / 24);
  return t("relative.days", { n: days });
};
